import { useCallback, useEffect, useState } from "react";
import {
  popupForPoints,
  popupForPolygon,
  popupForPolygonComp,
  popupForRoad,
  popupForRoadComp,
} from "../popupTemplate";
import useEsri from "../hooks/useEsri";
import {
  GIS_PM_POINTS_FIXED_ID,
  GIS_PM_POINTS_SUPPLEMENTARY_ID,
  GIS_PM_POLYGONS_ID,
  GIS_PM_POLYGONS_COMP_ID,
  GIS_PM_ROADS_ID,
  GIS_PM_ROADS_COMP_ID,
} from "../settings";

const useRequestedDataLayer = ({ serviceResponse }) => {
  const [loading, setLoading] = useState(true);
  const [fixed_measurement_point, setFixedMeasurementPoint] = useState();
  const [supplementary_measurement_point, setSupplementaryMeasurementPoint] =
    useState();
  const [background, setBackground] = useState();
  const [background_comp, setBackgroundComp] = useState();
  const [roadside, setRoadside] = useState();
  const [roadside_comp, setRoadsideComp] = useState();
  const { rendererStyles, highlightFeatureItem, removeAllDataLayers } =
    useEsri();

  const resetRequestedLayerState = useCallback(() => {
    removeAllDataLayers();
    setBackground(null);
    setBackgroundComp(null);
    setRoadside(null);
    setRoadsideComp(null);
    setFixedMeasurementPoint(null);
    setSupplementaryMeasurementPoint(null);
  }, [removeAllDataLayers]);

  const rendererBackground = useCallback(
    async ({
      gpServiceResponse,
      renderOptions,
      pollutant_metric_name,
      gpServiceInfoOptions,
      getSelectedQueryLayer,
      popupTitle,
    }) => {
      const { background_service, background_id, background_name } =
        gpServiceResponse;

      const backgroundRenderOptions = {
        ...renderOptions,
        layerServiceURL: background_service,
        layerUniqueId: GIS_PM_POLYGONS_ID,
        layerName: "Modelled Background",
        layerOptions: {
          popupTemplate: {
            title: () => {
              return popupTitle;
            },
            content: (feature) => {
              return popupForPolygon(
                feature,
                getSelectedQueryLayer,
                pollutant_metric_name
              );
            },
          },
        },
      };
      await rendererStyles(backgroundRenderOptions);
      return {
        service: background_service,
        service_id: background_id,
        service_name: background_name,
        ...gpServiceInfoOptions,
      };
    },
    [rendererStyles]
  );

  const rendererBackgroundComp = useCallback(
    async ({
      gpServiceResponse,
      renderOptions,
      pollutant_metric_name,
      gpServiceInfoOptions,
      getSelectedQueryLayer,
      popupTitle,
    }) => {
      const { background_comp_service, background_comp_id, background_comp_name } =
        gpServiceResponse;

      const backgroundCompRenderOptions = {
        ...renderOptions,
        layerServiceURL: background_comp_service,
        layerUniqueId: GIS_PM_POLYGONS_COMP_ID,
        layerName: "Modelled Compliance Background",
        layerOptions: {
          popupTemplate: {
            title: () => {
              return popupTitle;
            },
            content: (feature) => {
              return popupForPolygonComp(
                feature,
                getSelectedQueryLayer,
                pollutant_metric_name
              );
            },
          },
        },
      };
      await rendererStyles(backgroundCompRenderOptions);
      return {
        service: background_comp_service,
        service_id: background_comp_id,
        service_name: background_comp_name,
        ...gpServiceInfoOptions,
      };
    },
    [rendererStyles]
  );

  const rendererRoadside = useCallback(
    async ({
      gpServiceResponse,
      renderOptions,
      pollutant_metric_name,
      gpServiceInfoOptions,
      getSelectedQueryLayer,
      popupTitle,
    }) => {
      const { roadside_service, roadside_id, roadside_name } =
        gpServiceResponse;
      const roadRenderOptions = {
        ...renderOptions,
        layerServiceURL: roadside_service,
        layerUniqueId: GIS_PM_ROADS_ID,
        layerName: "Modelled Roadside",
        layerOptions: {
          popupTemplate: {
            title: () => {
              return popupTitle;
            },
            content: (feature) => {
              return popupForRoad(
                feature,
                getSelectedQueryLayer,
                pollutant_metric_name
              );
            },
          },
        },
      };
      await rendererStyles(roadRenderOptions);
      return {
        service: roadside_service,
        service_id: roadside_id,
        service_name: roadside_name,
        ...gpServiceInfoOptions,
      };
    },
    [rendererStyles]
  );

  const rendererRoadsideComp = useCallback(
    async ({
      gpServiceResponse,
      renderOptions,
      pollutant_metric_name,
      gpServiceInfoOptions,
      getSelectedQueryLayer,
      popupTitle,
    }) => {
      const { roadside_comp_service, roadside_comp_id, roadside_comp_name } =
        gpServiceResponse;
      const roadsideCompRenderOptions = {
        ...renderOptions,
        layerServiceURL: roadside_comp_service,
        layerUniqueId: GIS_PM_ROADS_COMP_ID,
        layerName: "Modelled Roadside Compliance",
        layerOptions: {
          popupTemplate: {
            title: () => {
              return popupTitle;
            },
            content: (feature) => {
              return popupForRoadComp(
                feature,
                getSelectedQueryLayer,
                pollutant_metric_name
              );
            },
          },
        },
      };
      await rendererStyles(roadsideCompRenderOptions);
      return {
        service: roadside_comp_service,
        service_id: roadside_comp_id,
        service_name: roadside_comp_name,
        ...gpServiceInfoOptions,
      };
    },
    [rendererStyles]
  );

  const rendererSupplementaryMeasurements = useCallback(
    async ({
      gpServiceResponse,
      renderOptions,
      pollutant_metric_name,
      gpServiceInfoOptions,
      getSelectedQueryLayer,
      popupTitle,
    }) => {
      const {
        supplementary_measurement_point_service,
        supplementary_measurement_point_id,
        supplementary_measurement_point_name,
      } = gpServiceResponse;
      const supplementaryPointRenderOptions = {
        ...renderOptions,
        layerServiceURL: supplementary_measurement_point_service,
        layerUniqueId: GIS_PM_POINTS_SUPPLEMENTARY_ID,
        layerName: "Supplementary Measurements",
        layerOptions: {
          popupTemplate: {
            title: () => {
              return popupTitle;
            },
            content: (feature) => {
              return popupForPoints(
                feature,
                getSelectedQueryLayer,
                pollutant_metric_name
              );
            },
          },
        },
      };
      await rendererStyles(supplementaryPointRenderOptions);
      return {
        service: supplementary_measurement_point_service,
        service_id: supplementary_measurement_point_id,
        service_name: supplementary_measurement_point_name,
        ...gpServiceInfoOptions,
      };
    },
    [rendererStyles]
  );

  const rendererFixedMeasurements = useCallback(
    async ({
      gpServiceResponse,
      renderOptions,
      pollutant_metric_name,
      gpServiceInfoOptions,
      getSelectedQueryLayer,
      popupTitle,
    }) => {
      const {
        fixed_measurement_point_service,
        fixed_measurement_point_id,
        fixed_measurement_point_name,
      } = gpServiceResponse;
      const fixedPointRenderOptions = {
        ...renderOptions,
        layerServiceURL: fixed_measurement_point_service,
        layerUniqueId: GIS_PM_POINTS_FIXED_ID,
        layerName: "Fixed Measurements",
        layerOptions: {
          popupTemplate: {
            title: () => {
              return popupTitle;
            },
            content: (feature) => {
              return popupForPoints(
                feature,
                getSelectedQueryLayer,
                pollutant_metric_name
              );
            },
          },
        },
      };
      await rendererStyles(fixedPointRenderOptions);
      return {
        service: fixed_measurement_point_service,
        service_id: fixed_measurement_point_id,
        service_name: fixed_measurement_point_name,
        ...gpServiceInfoOptions,
      };
    },
    [rendererStyles]
  );

  useEffect(() => {
    if (!serviceResponse) return;
    const {
      background,
      background_comp,
      roadside,
      roadside_comp,
      fixed_measurement_point,
      supplementary_measurement_point,
    } = serviceResponse.gpServiceResponse;
    const { highlightLayerUrl, gpServiceInfoOptions } = serviceResponse;
    const { unique_id: selectedZoneLaUniqueCode } = gpServiceInfoOptions;

    resetRequestedLayerState();
    setLoading(true);

    (async () => {
      if (background) {
        const bgRenderResponse = await rendererBackground(serviceResponse);
        setBackground(() => {
          return {
            ...bgRenderResponse,
          };
        });
      }

      if (background_comp) {
        const bgCompRenderResponse = await rendererBackgroundComp(serviceResponse);
        setBackgroundComp(() => {
          return {
            ...bgCompRenderResponse,
          };
        });
      }

      if (roadside) {
        const roadRenderResponse = await rendererRoadside(serviceResponse);
        setRoadside(() => {
          return {
            ...roadRenderResponse,
          };
        });
      }

      if (roadside_comp) {
        const roadCompRenderResponse = await rendererRoadsideComp(serviceResponse);
        setRoadsideComp(() => {
          return {
            ...roadCompRenderResponse,
          };
        });
      }

      if (supplementary_measurement_point) {
        const supplementaryMeasureResponse =
          await rendererSupplementaryMeasurements(serviceResponse);
        setSupplementaryMeasurementPoint(() => {
          return {
            ...supplementaryMeasureResponse,
          };
        });
      }

      if (fixed_measurement_point) {
        const fixedMeasureResponse = await rendererFixedMeasurements(
          serviceResponse
        );
        setFixedMeasurementPoint(() => {
          return {
            ...fixedMeasureResponse,
          };
        });
      }

      highlightFeatureItem(highlightLayerUrl, selectedZoneLaUniqueCode);
      setLoading(false);
    })();

    return () => {
      /* clean up */
    };
  }, [
    serviceResponse,
    highlightFeatureItem,
    resetRequestedLayerState,
    rendererBackground,
    rendererRoadside,
    rendererBackgroundComp,
    rendererRoadsideComp,
    rendererSupplementaryMeasurements,
    rendererFixedMeasurements,
  ]);

  return {
    loading,
    fixed_measurement_point,
    supplementary_measurement_point,
    roadside,
    roadside_comp,
    background,
    background_comp,
  };
};

export default useRequestedDataLayer;
